import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/Button";
import { getVapidStatus, getWebPushSchedule, readPushSchedule, saveWebPushSchedule, type PushSchedule, type VapidStatus } from "./api";
import { disableWebPush, enableWebPush } from "./register";

export function WebPushSettings(): JSX.Element {
  const { t } = useTranslation();
  const [vapid, setVapid] = useState<VapidStatus | null>(null);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [subscribed, setSubscribed] = useState(false);
  const [schedule, setSchedule] = useState<PushSchedule>({ timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC", quiet_start: "22:00", quiet_end: "08:00", enabled: true });

  useEffect(() => {
    void getVapidStatus().then(setVapid).catch((err) => setError(err instanceof Error ? err.message : String(err)));
    void getWebPushSchedule().then((result) => setSchedule(readPushSchedule(result?.schedule))).catch((err) => setError(err instanceof Error ? err.message : String(err)));
    if ("serviceWorker" in navigator) {
      void navigator.serviceWorker.getRegistration("/sw-web-push.js").then(async (registration) => {
        const sub = await registration?.pushManager.getSubscription();
        setSubscribed(Boolean(sub));
      });
    }
  }, []);

  const toggle = async () => {
    setBusy(true); setError(null); setMessage(null);
    try {
      if (subscribed) {
        await disableWebPush();
        setSubscribed(false);
        setMessage("profilePage.webPush.disabled");
      } else {
        const result = await enableWebPush(vapid?.public_key ?? undefined);
        if (!result.ok) { setError(result.reason); setSubscribed(false); }
        else { setSubscribed(true); setMessage("profilePage.webPush.enabled"); }
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally { setBusy(false); }
  };

  const saveHours = async () => {
    setBusy(true); setError(null); setMessage(null);
    try {
      const result = await saveWebPushSchedule(schedule);
      setSchedule(readPushSchedule(result?.schedule));
      setMessage("profilePage.webPush.scheduleSaved");
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally { setBusy(false); }
  };

  const configured = vapid?.configured === true;
  return (
    <section className="space-y-4" aria-labelledby="web-push-heading">
      <div>
        <h2 id="web-push-heading" className="text-md font-semibold">{t("profilePage.webPush.title")}</h2>
        <p className="text-sm text-fg-muted">{t("profilePage.webPush.description")}</p>
      </div>
      {vapid && !configured ? (
        <p role="status" className="text-sm text-warning">
          {t("profilePage.webPush.notConfigured")}
        </p>
      ) : null}
      {error ? <p role="alert" className="text-danger text-sm">{error}</p> : null}
      {message ? <p role="status" className="text-sm">{t(message)}</p> : null}
      <Button type="button" disabled={busy || (!configured && !subscribed)} onClick={() => void toggle()}>
        {t(subscribed ? "profilePage.webPush.disable" : "profilePage.webPush.enable")}
      </Button>
      <div className="grid gap-3 md:grid-cols-3 text-sm">
        <label className="flex flex-col gap-1">
          <span>{t("profilePage.webPush.timezone")}</span>
          <input className="h-10 rounded-md border border-border-default bg-canvas px-3" value={schedule.timezone} onChange={(e) => setSchedule({ ...schedule, timezone: e.target.value })} aria-label={t("profilePage.webPush.timezoneLabel")} />
        </label>
        <label className="flex flex-col gap-1">
          <span>{t("profilePage.webPush.quietStart")}</span>
          <input type="time" className="h-10 rounded-md border border-border-default bg-canvas px-3" value={schedule.quiet_start ?? ""} onChange={(e) => setSchedule({ ...schedule, quiet_start: e.target.value || null })} aria-label={t("profilePage.webPush.quietStartLabel")} />
        </label>
        <label className="flex flex-col gap-1">
          <span>{t("profilePage.webPush.quietEnd")}</span>
          <input type="time" className="h-10 rounded-md border border-border-default bg-canvas px-3" value={schedule.quiet_end ?? ""} onChange={(e) => setSchedule({ ...schedule, quiet_end: e.target.value || null })} aria-label={t("profilePage.webPush.quietEndLabel")} />
        </label>
      </div>
      <label className="flex items-center gap-2 text-sm">
        <input type="checkbox" checked={schedule.enabled} onChange={(e) => setSchedule({ ...schedule, enabled: e.target.checked })} />
        {t("profilePage.webPush.applySchedule")}
      </label>
      <Button type="button" variant="secondary" disabled={busy} onClick={() => void saveHours()}>{t("profilePage.webPush.saveSchedule")}</Button>
    </section>
  );
}
